'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormProps } from 'tdesign-react';
import { ConfigProvider, Form, MessagePlugin } from 'tdesign-react';
import enConfig from 'tdesign-react/es/locale/en_US';
import { useIntlGlobalData, useMedia } from '@ssv-website/hooks';
import { getLanguageSwitchMeta } from '../i18n/config';
import { I18nProvider } from '../i18n/I18nProvider';
import { getUploadPreviewUrl } from './AvatarUploadField';
import {
  getConferenceGuestDetail,
  registerConference2026,
  updateConferenceGuest,
} from './api';
import {
  AVATAR_UPLOAD_REQUIRED_MESSAGE,
  DESKTOP_SUBMIT_BUTTON_TEXT,
  DUPLICATE_REGISTER_MESSAGE,
  EDIT_BUTTON_TEXT,
  MOBILE_SUBMIT_BUTTON_TEXT,
  PAGE_LOADING_TEXT,
  PROFILE_UPDATE_NO_CHANGE_MESSAGE,
  PROFILE_UPDATE_REVIEWING_BUTTON_TEXT,
  PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE,
  SUBMIT_LOGIN_REQUIRED_MESSAGE,
  SUBMIT_SUCCESS_MESSAGE,
} from './config';
import { DesktopSignupPage } from './DesktopSignupPage';
import {
  buildConferenceRegisterPayload,
  buildGuestDetailInitialValues,
  buildStaticInitialValues,
  isConferenceRegisterPayloadChanged,
  isSignupFormSubmittable,
  mergeInitialValues,
} from './form-helpers';
import { MobileSignupPage } from './MobileSignupPage';
import type {
  ConferenceGuestDetail,
  SignupFormValues,
  SignupPageProps,
} from './types';
import '../s-2026-signup/desktop.css';
import '../s-2026-signup/mobile.css';

// 覆盖 TDesign Upload 组件的默认文案
if (enConfig?.upload?.triggerUploadText) {
  enConfig.upload.triggerUploadText.image = 'Click to upload image';
}

const PROFILE_UPDATE_REVIEW_STATUS_PENDING = 1;
const PROFILE_UPDATE_REVIEW_STATUS_APPROVED = 2;

type ProfileUpdateReviewState = Pick<
  ConferenceGuestDetail,
  'hasPendingProfileUpdate' | 'profileUpdateReviewStatus'
>;

const getProfileUpdateReviewStatus = (
  detail?: ProfileUpdateReviewState | null
) =>
  detail?.hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEW_STATUS_PENDING
    : detail?.profileUpdateReviewStatus;

// Keep the same blocking rule as the conference-chat profile editor.
const isProfileUpdateBlocked = (detail?: ProfileUpdateReviewState | null) => {
  const reviewStatus = getProfileUpdateReviewStatus(detail);
  return (
    reviewStatus === PROFILE_UPDATE_REVIEW_STATUS_PENDING ||
    reviewStatus === PROFILE_UPDATE_REVIEW_STATUS_APPROVED
  );
};

function SignupPageLoading({ isMobile }: { isMobile: boolean }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-white"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={PAGE_LOADING_TEXT}
    >
      <span
        aria-hidden="true"
        className={`inline-block animate-spin rounded-full border-[#0052D9]/20 border-t-[#0052D9] ${
          isMobile ? 'h-5 w-5 border-2' : 'h-7 w-7 border-[3px]'
        }`}
      />
      <span className="sr-only">{PAGE_LOADING_TEXT}</span>
    </div>
  );
}

export const SSVMeeting2026SignupEnPage = ({
  heroBackgroundSrc,
  heroIllustration,
  heroIllustrationAlt = '',
  currentLang = 'en',
  enableLanguageSwitch = true,
  initialIsMobile = false,
}: SignupPageProps) => {
  const router = useRouter();
  const isConferenceChatEditEntry =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('from') ===
      'conference-chat';
  const [form] = Form.useForm();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingSubmittedRegistration, setIsEditingSubmittedRegistration] =
    useState(false);
  // Whether submission just succeeded (controls the success header display)
  const [isSubmitSuccessState, setIsSubmitSuccessState] = useState(false);
  const [isProfileUpdateSubmitSuccess, setIsProfileUpdateSubmitSuccess] =
    useState(false);
  const [hasLocalProfileUpdatePending, setHasLocalProfileUpdatePending] =
    useState(false);
  const [guestDetail, setGuestDetail] = useState<ConferenceGuestDetail | null>(
    null
  );
  const [isLoadingGuestDetail, setIsLoadingGuestDetail] = useState(false);
  const [guestDetailRequestStatus, setGuestDetailRequestStatus] = useState<
    'idle' | 'loading' | 'done'
  >('idle');
  const { isLogin, isUserInfoReady, userInfo } = useIntlGlobalData();
  const isEditingFromClickRef = useRef(false);
  const mediaIsMobile = useMedia('mobile');
  const isMobile = mediaIsMobile ?? initialIsMobile;
  const loginEmail = String(userInfo?.email ?? '').trim();
  const hasExistingRegistration = Boolean(guestDetail?.registerId);
  const hasPendingProfileUpdate =
    hasLocalProfileUpdatePending || isProfileUpdateBlocked(guestDetail);
  const isProfileUpdateMode =
    isConferenceChatEditEntry && hasExistingRegistration;
  const isEditingExistingRegistration =
    hasExistingRegistration && isEditingSubmittedRegistration;
  const isReadonlySubmittedState =
    hasExistingRegistration && !isEditingSubmittedRegistration;
  const isEmailReadonly = Boolean(loginEmail);

  const getResolvedFormValues = (values?: Partial<SignupFormValues>) =>
    mergeInitialValues(
      buildStaticInitialValues(userInfo),
      guestDetail ? buildGuestDetailInitialValues(guestDetail) : undefined,
      values
    );

  const getInitialResolvedFormValues = () =>
    mergeInitialValues(
      buildStaticInitialValues(userInfo),
      guestDetail ? buildGuestDetailInitialValues(guestDetail) : undefined
    );

  const getCurrentResolvedFormValues = () =>
    getResolvedFormValues(
      (form.getFieldsValue(true) as Partial<SignupFormValues>) ?? {}
    );

  const syncSubmitState = () => {
    const resolvedValues = getCurrentResolvedFormValues();

    setIsSubmitDisabled(!isSignupFormSubmittable(resolvedValues));
  };

  useLayoutEffect(() => {
    const initialValues = getResolvedFormValues();

    // initialData={readonlyValues} on Form handles first-time population.
    // Defer setFieldsValue via requestAnimationFrame to ensure Form's internal store
    // is fully initialized before we set values (especially for readonly→edit transition).
    if (typeof form.setFieldsValue === 'function') {
      window.requestAnimationFrame(() => {
        form.setFieldsValue(initialValues);
        form.clearValidate();
      });
    }

    setIsSubmitDisabled(!isSignupFormSubmittable(initialValues));
  }, [form, guestDetail, isEditingSubmittedRegistration, userInfo]);

  useEffect(() => {
    let isCancelled = false;

    if (!isUserInfoReady) {
      setIsLoadingGuestDetail(false);
      setGuestDetailRequestStatus('idle');
      return () => {
        isCancelled = true;
      };
    }

    if (!isLogin) {
      setGuestDetail(null);
      setIsLoadingGuestDetail(false);
      setIsEditingSubmittedRegistration(false);
      setGuestDetailRequestStatus('done');
      return () => {
        isCancelled = true;
      };
    }

    const loadGuestDetail = async () => {
      setIsLoadingGuestDetail(true);
      setGuestDetailRequestStatus('loading');
      try {
        const response = await getConferenceGuestDetail();
        if (isCancelled) {
          return;
        }

        if (response.code === 0 && response.data) {
          setGuestDetail(response.data);
          if (
            isConferenceChatEditEntry &&
            !isProfileUpdateBlocked(response.data)
          ) {
            // The chat profile entry should open the existing registration page directly in edit mode.
            isEditingFromClickRef.current = true;
            setIsEditingSubmittedRegistration(true);
          }
        }
      } catch (error: any) {
        // If the page already redirected (403/401 triggers redirect at a lower level), skip
        if (window.location.pathname.includes('/login')) {
          return;
        }
        if (!isCancelled) {
          console.error('Failed to load oversea registration detail', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingGuestDetail(false);
          setGuestDetailRequestStatus('done');
        }
      }
    };

    loadGuestDetail();

    return () => {
      isCancelled = true;
    };
  }, [isConferenceChatEditEntry, isLogin, isUserInfoReady, loginEmail]);

  const onPrimaryAction = () => {
    if (isReadonlySubmittedState) {
      isEditingFromClickRef.current = true;
      setIsEditingSubmittedRegistration(true);
      setIsSubmitSuccessState(false); // Reset submit success state when entering edit mode
      setIsProfileUpdateSubmitSuccess(false);
      setHasLocalProfileUpdatePending(false);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
      return;
    }

    form.submit();
  };

  const onSubmit: FormProps['onSubmit'] = async (context) => {
    const submitValues = getCurrentResolvedFormValues();

    if (context.firstError) {
      return;
    }

    if (!isLogin) {
      MessagePlugin.warning({
        content: SUBMIT_LOGIN_REQUIRED_MESSAGE,
        duration: 2000,
      });
      return;
    }

    if (isReadonlySubmittedState) {
      return;
    }

    const payload = buildConferenceRegisterPayload(submitValues);
    const displayPayload = {
      ...payload,
      avatar: getUploadPreviewUrl(submitValues.avatar) || payload.avatar,
    };

    if (isProfileUpdateMode && guestDetail) {
      const initialPayload = buildConferenceRegisterPayload(
        getInitialResolvedFormValues()
      );

      if (!isConferenceRegisterPayloadChanged(payload, initialPayload)) {
        MessagePlugin.info({
          content: PROFILE_UPDATE_NO_CHANGE_MESSAGE,
          duration: 2000,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (
        (isEditingExistingRegistration || isProfileUpdateMode) &&
        guestDetail
      ) {
        const result = await updateConferenceGuest(payload);

        setGuestDetail({
          ...guestDetail,
          ...displayPayload,
          registerId: result.registerId,
          status: result.status,
          updateTime: result.updateTime,
        });

        // Re-fetch detail to ensure server-processed fields (e.g. avatar URL) are up to date
        try {
          const response = await getConferenceGuestDetail();
          if (response.code === 0 && response.data) {
            setGuestDetail(response.data);
          }
        } catch {
          // noop — we already have the optimistic update above
        }
      } else {
        const result = await registerConference2026(payload);

        setGuestDetail({
          ...displayPayload,
          registerId: result.registerId,
          status: result.status,
          createTime: result.createTime,
          updateTime: result.createTime,
        });
      }
      const submittedAsProfileUpdate = isProfileUpdateMode;

      setIsEditingSubmittedRegistration(false);
      setIsSubmitSuccessState(true); // Mark as submit success state
      setIsProfileUpdateSubmitSuccess(submittedAsProfileUpdate);
      // Chat profile updates create a backend review record; lock locally first so cached detail cannot briefly re-enable editing.
      setHasLocalProfileUpdatePending(submittedAsProfileUpdate);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
      MessagePlugin.success({
        content: submittedAsProfileUpdate
          ? PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE
          : SUBMIT_SUCCESS_MESSAGE,
        duration: 2000,
      });
    } catch (error) {
      if (
        !isEditingExistingRegistration &&
        error instanceof Error &&
        error.message === DUPLICATE_REGISTER_MESSAGE
      ) {
        try {
          const response = await getConferenceGuestDetail();
          if (response.code === 0 && response.data) {
            setGuestDetail(response.data);
            setIsEditingSubmittedRegistration(false);
            setIsSubmitSuccessState(true); // Mark as submit success state
          }
        } catch {
          // noop
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    // If entering edit mode from the readonly submitted state, cancel returns to the readonly view
    if (isEditingFromClickRef.current) {
      isEditingFromClickRef.current = false;
      setIsEditingSubmittedRegistration(false);
      return;
    }
    // Otherwise (filling form after login for the first time), navigate to the event info page
    router.push('/case/activity/summit');
  };

  const displayedAccount = loginEmail;
  const { langLabel, targetLang } = getLanguageSwitchMeta(currentLang);
  const readonlyValues = getResolvedFormValues();
  const shouldShowGuestDetailPageLoading =
    !isUserInfoReady ||
    (isLogin && guestDetail === null && guestDetailRequestStatus !== 'done');
  const finalIsSubmitDisabled =
    isLoadingGuestDetail ||
    (hasPendingProfileUpdate
      ? true
      : isReadonlySubmittedState
      ? false
      : isSubmitDisabled);
  const primaryButtonText = hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEWING_BUTTON_TEXT
    : isReadonlySubmittedState
    ? EDIT_BUTTON_TEXT
    : DESKTOP_SUBMIT_BUTTON_TEXT;
  const mobilePrimaryButtonText = hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEWING_BUTTON_TEXT
    : isReadonlySubmittedState
    ? EDIT_BUTTON_TEXT
    : MOBILE_SUBMIT_BUTTON_TEXT;
  const submitSuccessTitle = isProfileUpdateSubmitSuccess
    ? PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE
    : SUBMIT_SUCCESS_MESSAGE;

  if (shouldShowGuestDetailPageLoading) {
    return (
      <ConfigProvider globalConfig={enConfig}>
        <SignupPageLoading isMobile={isMobile} />
      </ConfigProvider>
    );
  }

  if (isMobile) {
    return (
      <ConfigProvider globalConfig={enConfig}>
        <I18nProvider defaultLocale={currentLang}>
          <MobileSignupPage
            displayedAccount={displayedAccount}
            form={form}
            heroBackgroundSrc={heroBackgroundSrc}
            heroIllustration={heroIllustration}
            heroIllustrationAlt={heroIllustrationAlt}
            isEnteringFromEdit={isEditingFromClickRef.current}
            isLogin={isLogin}
            isReadonlySubmittedState={isReadonlySubmittedState}
            isSubmitDisabled={finalIsSubmitDisabled}
            isSubmitting={isSubmitting}
            isSubmitSuccessState={isSubmitSuccessState}
            isEmailReadonly={isEmailReadonly}
            onPrimaryAction={onPrimaryAction}
            onSubmit={onSubmit}
            readonlyValues={readonlyValues}
            submitButtonText={mobilePrimaryButtonText}
            submitSuccessTitle={submitSuccessTitle}
            syncSubmitState={syncSubmitState}
          />
        </I18nProvider>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider globalConfig={enConfig}>
      <DesktopSignupPage
        displayedAccount={displayedAccount}
        enableLanguageSwitch={enableLanguageSwitch}
        form={form}
        hasTeam={false}
        heroBackgroundSrc={heroBackgroundSrc}
        isReadonlySubmittedState={isReadonlySubmittedState}
        isSubmitDisabled={finalIsSubmitDisabled}
        isSubmitting={isSubmitting}
        isSubmitSuccessState={isSubmitSuccessState}
        isEmailReadonly={isEmailReadonly}
        langLabel={langLabel}
        onCancel={onCancel}
        onPrimaryAction={onPrimaryAction}
        onSubmit={onSubmit}
        readonlyValues={readonlyValues}
        submitButtonText={primaryButtonText}
        submitSuccessTitle={submitSuccessTitle}
        syncSubmitState={syncSubmitState}
        targetLang={targetLang}
      />
    </ConfigProvider>
  );
};
